"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupV2Props {
  name: string;
  value: string;
  onValueChange: (_value: string) => void;
  options: RadioOption[];
  label?: string;
  helperText?: string;
  errorText?: string;
  direction?: "vertical" | "horizontal";
  className?: string;
}

export function RadioGroupV2({
  name,
  value,
  onValueChange,
  options,
  label,
  helperText,
  errorText,
  direction = "vertical",
  className,
}: RadioGroupV2Props) {
  const uid = React.useId();
  const groupLabelId = label ? `${uid}-label` : undefined;
  const errorId = errorText ? `${uid}-error` : undefined;
  const helperId = !errorText && helperText ? `${uid}-helper` : undefined;
  const describedBy =
    [errorId, helperId].filter(Boolean).join(" ") || undefined;

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    const keys: Record<string, number> = {
      ArrowDown: 1,
      ArrowRight: 1,
      ArrowUp: -1,
      ArrowLeft: -1,
    };
    const dir = keys[e.key];
    if (dir == null) return;
    e.preventDefault();

    // Find next enabled option
    let next = idx + dir;
    while (next >= 0 && next < options.length) {
      if (!options[next].disabled) {
        onValueChange(options[next].value);
        // Focus the radio
        const el = document.querySelector<HTMLInputElement>(
          `input[name="${name}"][value="${options[next].value}"]`
        );
        el?.focus();
        return;
      }
      next += dir;
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <span
          id={groupLabelId}
          className="block text-xs font-semibold text-[var(--v2-text-strong)]"
        >
          {label}
        </span>
      )}

      <div
        role="radiogroup"
        aria-labelledby={groupLabelId}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        className={cn(
          "flex gap-3",
          direction === "vertical" ? "flex-col" : "flex-row flex-wrap"
        )}
      >
        {options.map((opt, idx) => {
          const checked = opt.value === value;
          const isInTab = checked || (!value && idx === 0);
          return (
            <label
              key={opt.value}
              className={cn(
                "group flex cursor-pointer items-start gap-2.5 rounded-[var(--v2-radius-md)] px-1 py-1 text-sm transition",
                opt.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={checked}
                disabled={opt.disabled}
                tabIndex={isInTab ? 0 : -1}
                onChange={() => onValueChange(opt.value)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                className="v2-focus-ring mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[var(--v2-accent)] disabled:cursor-not-allowed"
              />
              <span>
                <span className="block font-medium text-[var(--v2-text-strong)]">
                  {opt.label}
                </span>
                {opt.description && (
                  <span className="block text-xs text-[var(--v2-text-muted)]">
                    {opt.description}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {errorText && (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {errorText}
        </span>
      )}
      {!errorText && helperText && (
        <span id={helperId} className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      )}
    </div>
  );
}
